import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from './SEOHead';

const Privacy = () => {
  return (
  <>
  <SEOHead
    title="Privacy Policy"
    description="How Cayda handles your data. Our privacy policy covers data collection, storage, and your rights."
    path="/privacy"
  />
  <div className="min-h-screen bg-cream">
    <header className="border-b border-ink-faint/20 bg-cream/80 backdrop-blur-xl">
      <div className="max-w-3xl mx-auto px-6 h-14 flex items-center">
        <Link to="/" className="text-base font-display font-semibold tracking-tight text-ink">Cayda</Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-2xl font-display font-bold text-ink tracking-tight mb-2">Privacy Policy</h1>
      <p className="text-xs font-display text-ink-faint mb-10">Last updated: March 2026</p>

      <div className="space-y-8 text-sm font-display text-ink-secondary leading-relaxed">
        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">Overview</h2>
          <p>Cayda is a product of Slowfast LLC, a Wyoming limited liability company ("we", "us", "our"). We operate the cayda.io website and the Cayda widget service. This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our service.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">Information We Collect</h2>
          <p className="mb-3">We collect the following types of information:</p>
          <p className="mb-2"><strong className="text-ink">Account information:</strong> When you create an account, we collect your name, email address, and authentication credentials through our authentication provider (Clerk).</p>
          <p className="mb-2"><strong className="text-ink">Usage data:</strong> We collect information about how you use the service, including websites you add, notes you create, and interactions within the dashboard.</p>
          <p className="mb-2"><strong className="text-ink">Widget data:</strong> When the Cayda widget is installed on a website, it collects page URLs, DOM element selectors for note positioning, and optional screenshots. The widget does not collect any data from website visitors — it only activates for authenticated team members.</p>
          <p><strong className="text-ink">Payment information:</strong> Payment processing is handled by Stripe. We do not store credit card numbers or bank account details on our servers.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">How We Use Your Information</h2>
          <p>We use the information we collect to provide and maintain the service, process transactions, send service-related communications, and improve the product. We do not sell your personal information to third parties.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">Data Storage & Security</h2>
          <p>Your data is stored securely using Supabase (hosted on AWS). We implement industry-standard security measures including encryption in transit (TLS), row-level security policies, and secure authentication tokens. Screenshots and note content are stored in our database and are only accessible to authorized team members.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">Third-Party Services</h2>
          <p className="mb-2">We use the following third-party services:</p>
          <p className="mb-1"><strong className="text-ink">Clerk</strong> — Authentication and user management</p>
          <p className="mb-1"><strong className="text-ink">Stripe</strong> — Payment processing</p>
          <p className="mb-1"><strong className="text-ink">Supabase</strong> — Database and storage</p>
          <p className="mb-1"><strong className="text-ink">Vercel</strong> — Website hosting and analytics</p>
          <p className="mb-1"><strong className="text-ink">Railway</strong> — API hosting</p>
          <p><strong className="text-ink">Google Analytics</strong> — Website usage analytics on the cayda.io dashboard and marketing pages. Google may set cookies (such as <code className="text-xs font-mono">_ga</code>) to distinguish unique visitors and track session information. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-ink underline underline-offset-2">Google's Privacy Policy</a> for details on how Google processes this data.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">Widget & Visitor Privacy</h2>
          <p>The Cayda widget is designed to be completely invisible to regular website visitors. It does not track visitors, set cookies on their browsers, or collect any visitor data. The widget only activates when an authenticated team member opens it.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">Data Retention & Deletion</h2>
          <p>You can delete your account and associated data at any time by contacting us. When a website is removed from Cayda, all associated notes, comments, and screenshots are permanently deleted. Free tier accounts with no activity for 12 months may be subject to data deletion after notice.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">Cookies</h2>
          <p>The Cayda dashboard uses essential cookies for authentication and session management. We also use Google Analytics, which sets cookies to help us understand how visitors use cayda.io. These cookies are limited to our marketing and dashboard pages and are not used for advertising. The embeddable widget does not set any cookies on your visitors' browsers.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">Your Rights</h2>
          <p>You have the right to access, correct, or delete your personal data. You can export your notes and data from the dashboard. To exercise these rights or for any privacy-related questions, contact us at privacy@cayda.io.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">Changes to This Policy</h2>
          <p>We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">Governing Law</h2>
          <p>This privacy policy is governed by and construed in accordance with the laws of the State of Wyoming, without regard to its conflict of law provisions.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">Contact</h2>
          <p>If you have questions about this privacy policy, contact us at <a href="mailto:privacy@cayda.io" className="text-ink underline underline-offset-2">privacy@cayda.io</a>.</p>
        </section>
      </div>
    </main>

    <footer className="border-t border-ink-faint/20 py-6 px-6 bg-cream">
      <div className="max-w-3xl mx-auto flex items-center justify-between">
        <Link to="/" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">← Back to Cayda</Link>
        <span className="text-xs font-display text-ink-faint">&copy; {new Date().getFullYear()} Slowfast LLC</span>
      </div>
    </footer>
  </div>
  </>
  );
};

export default Privacy;
